import * as Plot from '@observablehq/plot';
import {useEffect, useRef} from 'react';
import {ParsedGraphConfig} from '../../types/GraphTypes';

interface GraphScatterChartProps {
  parsedConfig: ParsedGraphConfig;
  colors: string[];
  xLabel: string;
  xDomain: [number | 'dataMin', number | 'dataMax'] | undefined;
}

export default function GraphScatterChart({parsedConfig, colors, xLabel, xDomain}: GraphScatterChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const {yAxes, dataSeries} = parsedConfig;

  useEffect(() => {
    if (!containerRef.current || dataSeries.length === 0) return;

    // Prepare data for Observable Plot - flatten all series into one array
    const plotData: Array<Record<string, number | string>> = [];
    dataSeries.forEach((series) => {
      series.data.forEach((point) => {
        plotData.push({
          x: point.x,
          y: point.y,
          series: series.name,
          seriesKey: series.key,
        });
      });
    });

    // Calculate domains
    const xMin = xDomain?.[0] === 'dataMin' ? undefined : (xDomain?.[0] as number | undefined);
    const xMax = xDomain?.[1] === 'dataMax' ? undefined : (xDomain?.[1] as number | undefined);

    // For scatter, use auto domain for Y
    const primaryYAxis = yAxes[0];

    // Build marks
    const marks: Plot.Markish[] = [
      Plot.gridX({stroke: '#e0e0e0', strokeDasharray: '3,3'}),
      Plot.gridY({stroke: '#e0e0e0', strokeDasharray: '3,3'}),
    ];

    // Add scatter marks for each series
    dataSeries.forEach((series, index) => {
      const color = colors[index % colors.length];
      const seriesData = plotData.filter((d) => d.seriesKey === series.key);
      marks.push(
        Plot.dot(seriesData, {
          x: 'x',
          y: 'y',
          fill: color,
          r: 4,
        }),
      );
    });

    // Create the plot
    const plotOptions: Plot.PlotOptions = {
      marks,
      x: {
        label: xLabel,
        domain: xMin !== undefined || xMax !== undefined ? [xMin ?? undefined, xMax ?? undefined] : undefined,
        type: 'linear',
      },
      y: {
        label: primaryYAxis?.config.label || 'Y Axis',
        type: 'linear',
      },
      marginTop: 40,
      marginRight: 30,
      marginBottom: 30,
      marginLeft: 20,
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    };

    const chart = Plot.plot(plotOptions);
    const container = containerRef.current;
    container.innerHTML = '';
    container.appendChild(chart);

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [parsedConfig, colors, xLabel, xDomain, yAxes, dataSeries]);

  return <div ref={containerRef} data-testid="graph-scatter-chart" style={{width: '100%', height: '100%'}} />;
}
